import React, { useMemo, useState } from 'react';

import { Box, Pagination, Skeleton, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';

import { withScoutTheme } from '@/components/ThemeContext/ThemeContext';

import ClassificationsApi from './API';
import ClassificationsPanel from './ClassificationsPanel';
import { FONT_FAMILY, PAGE_BACKGROUND, PRIMARY_GREEN } from './colors';
import EmptyJobsState from './EmptyJobsState';
import Footer from './Footer';
import JobCard from './JobCard';
import Navbar from './Navbar';
import SearchInput from './SearchInput';
import SignUpCard from './SignUpCard';

import type { PublicClassification } from './types';

const positiveInteger = (value: string | null) => {
  const number = Number(value);
  return value && Number.isInteger(number) && number > 0 ? number : null;
};

const initialFilters = () => {
  const params = new URLSearchParams(window.location.search);

  return {
    classificationId: positiveInteger(params.get('id')),
    page: positiveInteger(params.get('page')) ?? 1,
    searchQuery: params.get('q') ?? ''
  };
};

const JobsListing = () => {
  const filters = useMemo(() => initialFilters(), []);
  const [selectedId, setSelectedId] = useState<number | null>(filters.classificationId);
  const [searchQuery, setSearchQuery] = useState(filters.searchQuery);
  const [page, setPage] = useState(filters.page);
  const [expandedRootIds, setExpandedRootIds] = useState<Set<number>>(() => new Set());
  const [childrenByRootId, setChildrenByRootId] = useState<Record<number, PublicClassification[]>>(
    () => ({})
  );

  const expandMutation = useMutation(
    async (rootId: number) => ClassificationsApi.getDetails(rootId),
    {
      onSuccess: (data, rootId) => {
        setChildrenByRootId((prev) => ({ ...prev, [rootId]: data.children }));
      }
    }
  );

  const jobsQuery = useQuery({
    queryKey: ['public-jobs-listing', selectedId, searchQuery, page],
    queryFn: async () => ClassificationsApi.getDetails(selectedId, searchQuery || undefined, page),
    keepPreviousData: true,
    onSuccess: (data) => {
      if (data.expandedRootId) {
        const { expandedRootId, children } = data;
        setExpandedRootIds((prev) => new Set(prev).add(expandedRootId));
        setChildrenByRootId((prev) => ({ ...prev, [expandedRootId]: children }));
      }
    }
  });

  const handleToggleExpand = (rootId: number) => {
    const alreadyExpanded = expandedRootIds.has(rootId);

    setExpandedRootIds((prev) => {
      const next = new Set(prev);
      if (alreadyExpanded) {
        next.delete(rootId);
      } else {
        next.add(rootId);
      }
      return next;
    });

    if (!alreadyExpanded && !childrenByRootId[rootId]) {
      expandMutation.mutate(rootId);
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedId((prev) => (prev === id ? null : id));
    setPage(1);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, nextPage: number) => {
    setPage(nextPage);
  };

  const expandingRootId = expandMutation.isLoading ? (expandMutation.variables ?? null) : null;
  const jobsLoading = jobsQuery.isFetching;

  if (jobsQuery.isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', padding: '64px' }}>
        <Skeleton animation="wave" height={80} />
        <Skeleton animation="wave" height={160} />
        <Skeleton animation="wave" height={160} />
      </Box>
    );
  }

  const listing = jobsQuery.data;
  const pageConfiguration = listing?.pageConfiguration;

  if (!listing || pageConfiguration == null) {
    return (
      <Box sx={{ minHeight: '100vh', padding: '64px' }}>
        <Typography role="alert">
          Unable to load available jobs. Please refresh the page.
        </Typography>
      </Box>
    );
  }

  const { classifications, jobs, totalPages } = listing;
  const {
    contactUrl,
    currentYear,
    footerLinkColumns,
    footerLinkUrls,
    phoneUrl,
    privacyPolicyUrl,
    signUpUrl,
    termsUrl
  } = pageConfiguration;

  return (
    <Box sx={{ backgroundColor: PAGE_BACKGROUND, minHeight: '100vh' }}>
      <Box sx={{ padding: '0 64px' }}>
        <Navbar contactUrl={contactUrl} />

        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 800,
            fontSize: '32px',
            color: PRIMARY_GREEN,
            maxWidth: '1152px',
            margin: '0 auto 24px'
          }}
        >
          Jobs
        </Typography>

        {jobs.length === 0 && classifications.length === 0 && searchQuery === '' ? (
          <Box sx={{ maxWidth: '1152px', margin: '0 auto', paddingBottom: '64px' }}>
            <EmptyJobsState />
          </Box>
        ) : (
          <Box
            sx={{
              maxWidth: '1152px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: '24px',
              paddingBottom: '64px'
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                minWidth: 0,
                opacity: jobsLoading ? 0.6 : 1
              }}
            >
              <SearchInput onSearch={handleSearch} />
              {jobs.length === 0 ? (
                <EmptyJobsState />
              ) : (
                jobs.map((job) => <JobCard key={job.id} job={job} />)
              )}

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: '8px' }}>
                  <Pagination
                    count={totalPages}
                    disabled={jobsLoading}
                    id="jobs-pagination"
                    onChange={handlePageChange}
                    page={page}
                  />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                width: { xs: '100%', md: '392px' },
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '24px'
              }}
            >
              <ClassificationsPanel
                childrenByRootId={childrenByRootId}
                expandedRootIds={expandedRootIds}
                expandingRootId={expandingRootId}
                onToggleExpand={handleToggleExpand}
                onToggleSelect={handleToggleSelect}
                rootClassifications={classifications}
                selectedId={selectedId}
              />
              <SignUpCard signUpUrl={signUpUrl} />
            </Box>
          </Box>
        )}
      </Box>

      <Footer
        currentYear={currentYear}
        linkColumns={footerLinkColumns}
        linkUrls={footerLinkUrls}
        phoneUrl={phoneUrl}
        privacyPolicyUrl={privacyPolicyUrl}
        termsUrl={termsUrl}
      />
    </Box>
  );
};

export default withScoutTheme(JobsListing);
